import { useCallback, useEffect, useState } from "react";

import {
  iniciarSesion as pedirLogin,
  registrarUsuario as pedirRegistro,
  registrarManejadorNoAutorizado,
} from "../api/client";
import type { Usuario } from "../api/types";

// Guarda la sesion (token Bearer + usuario) en localStorage. Adapta
// `useToken` del hermano (token unico/ADMIN_TOKEN) a cuentas reales via
// /api/v1/auth (register/login).

const CLAVE_TOKEN = "supermarketing.token";
const CLAVE_USUARIO = "supermarketing.usuario";

function leerUsuarioGuardado(): Usuario | null {
  try {
    const crudo = localStorage.getItem(CLAVE_USUARIO);
    return crudo ? (JSON.parse(crudo) as Usuario) : null;
  } catch {
    return null;
  }
}

export interface Sesion {
  token: string;
  usuario: Usuario | null;
  cargando: boolean;
  error: string | null;
  iniciarSesion: (email: string, password: string) => Promise<void>;
  registrar: (email: string, password: string) => Promise<void>;
  cerrarSesion: () => void;
}

export function useSesion(): Sesion {
  const [token, setToken] = useState<string>(() => {
    try {
      return localStorage.getItem(CLAVE_TOKEN) ?? "";
    } catch {
      return "";
    }
  });
  const [usuario, setUsuario] = useState<Usuario | null>(leerUsuarioGuardado);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardarSesion = useCallback(
    (nuevoToken: string, nuevoUsuario: Usuario): void => {
      localStorage.setItem(CLAVE_TOKEN, nuevoToken);
      localStorage.setItem(CLAVE_USUARIO, JSON.stringify(nuevoUsuario));
      setToken(nuevoToken);
      setUsuario(nuevoUsuario);
    },
    [],
  );

  const cerrarSesion = useCallback((): void => {
    localStorage.removeItem(CLAVE_TOKEN);
    localStorage.removeItem(CLAVE_USUARIO);
    setToken("");
    setUsuario(null);
  }, []);

  // Manejo central del 401: cualquier request con Bearer invalido/vencido
  // cierra la sesion y la app vuelve a mostrar el login.
  useEffect(() => {
    registrarManejadorNoAutorizado(cerrarSesion);
    return () => registrarManejadorNoAutorizado(null);
  }, [cerrarSesion]);

  const ejecutarLogin = useCallback(
    async (email: string, password: string): Promise<void> => {
      setCargando(true);
      setError(null);
      try {
        const respuesta = await pedirLogin(email, password);
        guardarSesion(respuesta.token, respuesta.user);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "No se pudo iniciar sesion",
        );
        throw err;
      } finally {
        setCargando(false);
      }
    },
    [guardarSesion],
  );

  const ejecutarRegistro = useCallback(
    async (email: string, password: string): Promise<void> => {
      setCargando(true);
      setError(null);
      try {
        const respuesta = await pedirRegistro(email, password);
        guardarSesion(respuesta.token, respuesta.user);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "No se pudo crear la cuenta",
        );
        throw err;
      } finally {
        setCargando(false);
      }
    },
    [guardarSesion],
  );

  return {
    token,
    usuario,
    cargando,
    error,
    iniciarSesion: ejecutarLogin,
    registrar: ejecutarRegistro,
    cerrarSesion,
  };
}
